import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../features/auth/AuthContext';
import API_BASE from '../services/apiBase';
import AuthSplitShell from '../components/AuthSplitShell';

const ERROR_MESSAGES = {
  google_denied: 'Google sign-in was cancelled.',
  google_not_configured: 'Google sign-in is not configured yet.',
  google_failed: 'Google sign-in failed. Please try again.',
  missing_code: 'Google did not return an authorization code.',
  email_not_verified: 'Your Google email must be verified.',
  account_inactive: 'This account is deactivated. Contact support.',
  use_admin_login: 'Administrator accounts must sign in at /admin/login.',
};

const GoogleAuthCallback = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const token = params.get('token');
    const err = params.get('error');

    if (err) {
      setError(ERROR_MESSAGES[err] || 'Google sign-in could not be completed.');
      return;
    }

    if (!token) {
      setError('Missing sign-in token. Please try again.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.data) {
          throw new Error(json.message || 'Could not load your account');
        }

        const u = json.data;
        const loggedInUser = {
          ...u,
          token,
          loginId: u.email,
          loginType: 'email',
          isLoggedIn: true,
          staffDepartment: u.staffDepartment || 'full',
        };

        if (!cancelled) {
          login(loggedInUser);
          if (u.role === 'staff') {
            navigate('/admin', { replace: true, state: { user: loggedInUser } });
          } else {
            navigate('/profile', { replace: true, state: { user: loggedInUser } });
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Google sign-in failed.');
      }
    })();

    return () => { cancelled = true; };
  }, [params, login, navigate]);

  return (
    <AuthSplitShell title="Signing you in" subtitle="Completing Google sign-in">
      <div className="auth-split-card" style={{ textAlign: 'center', padding: '2rem 1.25rem' }}>
        {error ? (
          <>
            <AlertCircle className="w-8 h-8" style={{ color: '#dc2626', margin: '0 auto 1rem' }} />
            <p style={{ margin: '0 0 1rem', color: '#64748b' }}>{error}</p>
            <Link to="/login" className="auth-split-links">Back to login</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 auth-split-spinner" style={{ margin: '0 auto 1rem' }} />
            <p style={{ margin: 0, color: '#64748b' }}>Please wait…</p>
          </>
        )}
      </div>
    </AuthSplitShell>
  );
};

export default GoogleAuthCallback;
